import React from "react";
import { useParams } from "react-router";
import { Button, Card, Link, LongTextLabel, Tooltip } from "@sscs/ui";
import { DownloadIcon, EditIcon } from "@sscs/ui/icons";
import { buildRlPurl, getClassName } from "@sscs/utils";
import { AddToCatalog, AllowedTo, ApprovalStatus } from "Components";
import { CATALOG_PUBLISH_CONDITION, PERMISSION } from "Constants";
import { useAppDispatch, useCatalogRoute, useDialog } from "Hooks";
import { DownloadDialog } from "Modules/download";
import { isApproved } from "Utils";
import useProjectsRoute from "../../../../hooks/useProjectsRoute";
import { versionDownloaded } from "../../services/softwareCatalog.api";
import { SoftwareCatalogItem } from "../../types";
import CardContent from "../CardContent/CardContent";
import CommonCardTitle from "../CardTitle/CardTitle";
import LikeDislike from "../LikeDislike/LikeDislike";
import styles from "./softwareCard.module.scss";

type SoftwareCardProps = {
  software: SoftwareCatalogItem;
};

const CardTitle = ({ software }: Pick<SoftwareCardProps, "software">) => {
  const { groupId } = useParams();
  const projectsRoute = useProjectsRoute(
    software.project_id,
    software.package_id,
  );
  const packageRoute = useCatalogRoute({
    platform: software.version.software_info.platform,
    packageId: software.package_id,
  });

  return (
    <CommonCardTitle
      fileName={software.version.file_name}
      architecture={software.version.software_info.architecture}
      approvalStatus={software.version.approval_status}
    >
      <div className={styles["name-and-approval"]}>
        <Link
          underline="always"
          to={packageRoute}
          fullWidth={false}
          className={styles.link}
        >
          <LongTextLabel label={software.version.software_info.product} />
        </Link>
        <AllowedTo permissions={[PERMISSION.CREATE_PACKAGE]} groupId={groupId}>
          <Tooltip title="Manage versions">
            <Link to={projectsRoute} className={styles.edit}>
              <EditIcon />
            </Link>
          </Tooltip>
        </AllowedTo>
        <div className={styles.approval}>
          <ApprovalStatus
            status={software.version.approval_status}
            info={software.version.approval_information}
            variant="text"
          />
        </div>
      </div>
      <LongTextLabel
        label={software.version.software_info.version}
        className={styles.version}
      />
    </CommonCardTitle>
  );
};

const CardActions = ({
  software,
}: Pick<SoftwareCardProps, "software">): React.JSX.Element => {
  const projectsRoute = useProjectsRoute(
    software.project_id,
    software.package_id,
  );
  const downloadDialog = useDialog();
  const dispatch = useAppDispatch();

  if (!isApproved(software.version.approval_status)) {
    return (
      <Card.Actions>
        <div className={styles["not-applicable"]}>
          <div className={styles["not-applicable-text"]}>
            {CATALOG_PUBLISH_CONDITION}
          </div>
          <Link to={projectsRoute} className={styles["approve-version-link"]}>
            Approve version
          </Link>
        </div>
      </Card.Actions>
    );
  }

  if (software.in_catalog && software.version) {
    return (
      <Card.Actions className={styles.actions}>
        <LikeDislike
          packageId={software.package_id}
          userLike={software.user_like}
          popularity={software.popularity}
        />
        <Button
          color="secondary"
          startIcon={<DownloadIcon />}
          onClick={downloadDialog.open}
        >
          Download
        </Button>
        <DownloadDialog
          onClose={downloadDialog.close}
          isOpen={downloadDialog.isOpen}
          fileID={software.version.version_id}
          fileType="VERSION"
          onDownload={() =>
            dispatch(
              versionDownloaded({
                packageId: software.package_id,
                versionId: software.version.version_id,
              }),
            )
          }
        />
      </Card.Actions>
    );
  } else {
    return (
      <Card.Actions className={styles.actions}>
        <AddToCatalog
          packageId={software.package_id}
          packageName={software.package_name}
          inCatalog={software.in_catalog}
          className={styles["add-to-catalog"]}
        />
      </Card.Actions>
    );
  }
};

const SoftwareCard = ({ software }: SoftwareCardProps): React.JSX.Element => {
  const purl = buildRlPurl(
    software.project_name,
    software.package_name,
    software.version.software_info.version,
  );

  return (
    <Card.Card
      className={getClassName(styles["software-card"], {
        [styles["not-in-catalog"]]: !software.in_catalog,
      })}
    >
      <CardTitle software={software} />
      <CardContent
        softwareInfo={software.version.software_info}
        description={software.description}
        purl={purl}
        downloadCount={software.download_count}
      />
      <CardActions software={software} />
    </Card.Card>
  );
};

export default SoftwareCard;
